import { RankingData, SearchResult } from '../types';
import { createClient } from '@supabase/supabase-js';
import { RANKING_LIMIT } from '../constants';

const supabaseUrl = process.env.REACT_APP_API_URL;
const supabaseKey = process.env.REACT_APP_API_KEY;

if (!supabaseUrl || !supabaseKey) {
  throw new Error("❌ 缺少 Supabase 配置，请检查 .env 文件");
}

const supabase = createClient(supabaseUrl, supabaseKey);


export const getRankingData = async (): Promise<RankingData[]> => {
  try {
    const response = await supabase
      .from('efpower')
      .select('*')
      .lte('Rank', RANKING_LIMIT);
    if (response.error) throw response.error;
    return response.data as RankingData[];
  } catch (error) {
    return []; 
  }
};

export const getRankingDate = async () => {
  const { data, error } = await supabase
    .from('efpower_date')
    .select('date')
    .single();

  if (error) {
    return null;
  }

  return data;
};

export const searchByListId = async (listId: number): Promise<SearchResult> => {
  try {
    const response = await supabase
      .from('efpower')
      .select('*')
      .eq('listId', listId);
    const data = response.data as RankingData[];
    return {
      found: !!data.length,
      data: data[0],
    };
  } catch (error) {
    return { found: false };
  }
};

export function convertEmojiToDatabaseFormat(str: string): string {
  const formattedStr = str.replace(/([0-9])\uFE0F/g, '$1').replace(/([0-9])\u20E3/g, '$1⃣');
  return formattedStr;
}

export function convertDatabaseFormatToEmoji(str: string): string {
  return str.replace(/([0-9])⃣/g, '$1️⃣');
}

export const searchByEns = async (ensName: string): Promise<SearchResult> => {
  try {
    const formattedEnsName = convertEmojiToDatabaseFormat(ensName);

    const response = await supabase
      .from('efpower')
      .select('*')
      .eq('ensName', formattedEnsName);
    const data = response.data as RankingData[];
    return {
      found: !!data.length,
      data: data[0],
    };
  } catch (error) {
    return { found: false };
  }
};

export const searchByAddress = async (address: string): Promise<SearchResult> => {
  try {
    const response = await supabase
      .from('efpower')
      .select('*')
      .eq('address', address);
    const data = response.data as RankingData[];
    return {
      found: !!data.length,
      data: data[0],
    };
  } catch (error) {
    return { found: false };
  }
};

export const smartSearch = async (input: string): Promise<SearchResult> => {
  const cleanInput = input.trim().toLowerCase();

  if (/^\d+$/.test(cleanInput)) {
    return searchByListId(parseInt(cleanInput));
  }

  if (/^0x[a-fA-F0-9]{40}$/i.test(cleanInput)) {
    return searchByAddress(cleanInput);
  }

  if (cleanInput.includes('.')) {
    return searchByEns(cleanInput);
  }

  const ensResult = await searchByEns(cleanInput);
  if (ensResult.found) {
    return ensResult;
  }

  try {
    const response = await supabase
      .from('efpower')
      .select('*')
      .in('address', [cleanInput]);
    const data = response.data as RankingData[];

    return {
      found: !!data.length,
      data: data[0]
    };
  } catch (error) {
    return { found: false };
  }
};

export const formatEFPower = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2
  }).format(value);
};

export const truncateEns = (ensName: string, maxLength: number = 30): string => {
  if (!ensName || !ensName.includes('.')) return ''
  if (ensName.length <= maxLength) return ensName;
  return ensName.slice(0, maxLength) + '...';
};

export const getTopEFPower = async (): Promise<number> => {
  try {
    const response = await supabase
      .from('efpower')
      .select('*')
      .eq('Rank', 1);
    const data = response.data as RankingData[];
    const topScore = data[0]?.EFPower || 1000000;
    return topScore;
  } catch (error) {
    return 100000000;
  }
}; 